<template>
    <div class="reservation-home-status">
        <div v-if="isAwaitingDriver" class="card card-floating reservation-card">
            <div class="reservation-card__title">{{ translate('reservaBuscandoChofer') }}</div>
            <div class="reservation-card__subtitle">{{ translate('reservaBuscandoChoferDetalle') }}</div>
            <loader :flag="true" :size="32" />
            <button
                v-if="canCancel"
                class="btn btn-secondary btn-lg btn-heavy reservation-card__cancel"
                :disabled="cancelLoading"
                @click="onCancelReservation">
                {{ cancelLoading ? 'Procesando...' : translate('cancelarReserva') }}
            </button>
        </div>

        <div v-else-if="isAwaitingPayment" class="card card-floating reservation-card reservation-card--accepted">
            <div class="reservation-card__badge">{{ translate('reservaAceptadaTitulo') }}</div>
            <div class="reservation-card__subtitle" v-if="driverName">
                {{ translate('chofer') }}: <strong>{{ driverName }}</strong>
            </div>
            <div class="reservation-card__datetime" v-if="reservation.fecha_hora_reserva">
                <svg-item size="20" icon="calendar" color="--main-color" />
                {{ formatDateTime(reservation.fecha_hora_reserva) }}
            </div>
            <div class="text-alert" v-if="paymentCountdown">{{ paymentCountdown }}</div>
            <div class="reservation-card__hint">{{ translate('reservaPagoHomeHint') }}</div>
            <button
                class="btn btn-primary btn-lg btn-heavy"
                :disabled="paymentExpired || payLoading"
                @click="onPay">
                {{ payLoading ? 'Procesando...' : translate('pagarReserva') }}
            </button>
            <button class="btn btn-link" @click="goReservas">{{ translate('misReservas') }}</button>
            <button
                v-if="canCancel"
                class="btn btn-secondary btn-lg btn-heavy reservation-card__cancel"
                :disabled="cancelLoading"
                @click="onCancelReservation">
                {{ cancelLoading ? 'Procesando...' : translate('cancelarReserva') }}
            </button>
        </div>

        <div v-else-if="paymentExpired" class="card card-floating reservation-card">
            <div class="reservation-card__title">{{ translate('reservaPagoVencidoTitulo') }}</div>
            <div class="reservation-card__subtitle">{{ translate('reservaPagoVencidoMensaje') }}</div>
            <button class="btn btn-primary btn-lg btn-heavy" @click="goReservas">{{ translate('misReservas') }}</button>
            <button
                v-if="canCancel"
                class="btn btn-secondary btn-lg btn-heavy reservation-card__cancel"
                :disabled="cancelLoading"
                @click="onCancelReservation">
                {{ cancelLoading ? 'Procesando...' : translate('cancelarReserva') }}
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import dialogs from '@/services/dialogs';
import { canCancelReservation } from '@/services/reservation';

export default {
    name: 'ReservationHomeStatus',
    data () {
        return {
            nowTs: Date.now(),
            payLoading: false,
            cancelLoading: false,
            _countdownTimer: null
        };
    },
    computed: {
        ...mapGetters({
            reservation: 'passenger/pendingReservation',
            settings: 'company/config',
            position: 'geolocation/actual'
        }),
        pagoReserva () {
            return this.reservation && (this.reservation.pago_reserva || this.reservation.pagoReserva);
        },
        isAwaitingDriver () {
            return this.pagoReserva && this.pagoReserva.status === 'awaiting_driver';
        },
        isAwaitingPayment () {
            return this.pagoReserva && this.pagoReserva.status === 'awaiting_payment' && !this.paymentExpired;
        },
        paymentExpired () {
            if (!this.pagoReserva || this.pagoReserva.status !== 'awaiting_payment') return false;
            return this.isDueExpired(this.pagoReserva.payment_due_at);
        },
        driverName () {
            const c = this.reservation && this.reservation.chofer && this.reservation.chofer.usuario;
            return c ? `${c.nombre} ${c.apellido}`.trim() : '';
        },
        canCancel () {
            return canCancelReservation(this.reservation);
        },
        paymentCountdown () {
            if (!this.pagoReserva || !this.pagoReserva.payment_due_at) return null;
            const d = new Date(this.pagoReserva.payment_due_at);
            if (isNaN(d.getTime())) return null;
            const diffMs = d.getTime() - this.nowTs;
            if (diffMs <= 0) return this.translate('reservaPagoVencidoCountdown');
            const totalSeconds = Math.floor(diffMs / 1000);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            const pad = (n) => (n < 10 ? '0' + n : '' + n);
            return this.translate('reservaPagoCountdown', { time: `${minutes}:${pad(seconds)}` });
        }
    },
    mounted () {
        this._countdownTimer = setInterval(() => {
            this.nowTs = Date.now();
        }, 1000);
    },
    beforeDestroy () {
        if (this._countdownTimer) {
            clearInterval(this._countdownTimer);
            this._countdownTimer = null;
        }
    },
    methods: {
        ...mapActions({
            crearPreferenciaReserva: 'passenger/crearPreferenciaReserva',
            cancelReservation: 'passenger/cancelReservation'
        }),
        isDueExpired (paymentDueAt) {
            if (!paymentDueAt) return true;
            const d = new Date(paymentDueAt);
            return isNaN(d.getTime()) || d.getTime() <= this.nowTs;
        },
        formatDateTime (fechaHora) {
            const d = new Date(fechaHora);
            return isNaN(d.getTime()) ? fechaHora : d.toLocaleString();
        },
        onPay () {
            if (!this.reservation || !this.pagoReserva || this.paymentExpired) return;
            this.payLoading = true;
            this.crearPreferenciaReserva({
                reserva_pago_id: this.pagoReserva.id,
                monto: this.pagoReserva.monto
            }).then((res) => {
                const body = (res && res.data !== undefined) ? res.data : res;
                if (body && body.init_point) {
                    window.location.href = body.init_point;
                } else {
                    dialogs.message(this.translate('problemaInesperado'), { duration: 10, estado: 'error' });
                }
            }).catch(() => {
                dialogs.message(this.translate('problemaInesperado'), { duration: 10, estado: 'error' });
            }).finally(() => {
                this.payLoading = false;
            });
        },
        goReservas () {
            this.$router.push({ name: 'reservas' });
        },
        onCancelReservation () {
            if (!this.reservation || !canCancelReservation(this.reservation) || this.cancelLoading) return;
            dialogs.confirm(
                this.translate('confirmarCancelacionReserva'),
                (index) => {
                    if (index !== 1) return;
                    this.cancelLoading = true;
                    this.cancelReservation({
                        reservation: this.reservation,
                        lat: this.position ? this.position.lat : 0,
                        lng: this.position ? this.position.lng : 0,
                        comentario: 'Cancelado por pasajero'
                    }).finally(() => {
                        this.cancelLoading = false;
                    });
                },
                this.translate('cancelarReserva'),
                [this.translate('si'), this.translate('no')]
            );
        }
    }
};
</script>

<style scoped>
.reservation-home-status {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 1rem;
    z-index: 3;
    pointer-events: auto;
}
.reservation-card {
    padding: 1.25rem;
    text-align: center;
}
.reservation-card--accepted {
    border: 2px solid var(--main-color, #2e7d32);
}
.reservation-card__badge {
    font-weight: bold;
    font-size: 1.1em;
    color: var(--main-color, #2e7d32);
    margin-bottom: 0.5rem;
}
.reservation-card__title {
    font-weight: bold;
    margin-bottom: 0.5rem;
}
.reservation-card__subtitle {
    margin-bottom: 0.75rem;
    font-size: 0.95em;
}
.reservation-card__datetime {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    margin-bottom: 0.5rem;
}
.reservation-card__hint {
    font-size: 0.85em;
    margin: 0.5rem 0 1rem;
    opacity: 0.85;
}
.text-alert {
    color: #c0392b;
    font-weight: bold;
    margin-bottom: 0.5rem;
}
.btn-link {
    margin-top: 0.5rem;
    background: transparent;
    border: none;
    color: var(--main-color);
    text-decoration: underline;
}
.reservation-card__cancel {
    width: 100%;
    margin-top: 0.75rem;
}
</style>



// WEBPACK FOOTER //
// src/components/elements/passenger/ReservationHomeStatus.vue